import { getCurrentCalcSnapshot, loadScenarioIntoForm } from './ui.js';

const scenariosCtx = {
    state: {},
    list: [],
    container: null,
    emptyEl: null,
    messageEl: null,
    isMetaListMode: false
};

const SCENARIO_VIEW_META = 'meta';
const SCENARIO_VIEW_FULL = 'full';
const CANONICAL_YOUNG_TYPE = 'subvention-young';

function normalizeCalcType(type) {
    const raw = String(type || '').trim();
    if (!raw) return raw;
    return raw;
}

function getAuthToken() {
    const root = document.querySelector('.calc-page');
    if (!root) return null;

    const token = root.getAttribute('data-auth-token');
    if (typeof token !== 'string') return null;

    const trimmed = token.trim();
    return trimmed || null;
}

function ensureMessageElement() {
    if (!scenariosCtx.container) return null;
    if (scenariosCtx.messageEl) return scenariosCtx.messageEl;

    const el = document.createElement('p');
    el.className = 'calc-scenarios-message';
    el.hidden = true;
    scenariosCtx.container.before(el);
    scenariosCtx.messageEl = el;
    return el;
}

function setMessage(text, tone = 'info') {
    const el = ensureMessageElement();
    if (!el) return;

    el.hidden = !text;
    el.textContent = text || '';
    el.dataset.tone = tone;
}

function normalizeScenario(raw) {
    const hasInputJson = hasOwn(raw, 'inputJson');
    const hasResultJson = hasOwn(raw, 'resultJson');

    return {
        id: raw.id,
        calcType: normalizeCalcType(raw.calcType),
        scenarioName: raw.scenarioName,
        inputJson: hasInputJson ? (raw.inputJson || {}) : null,
        resultJson: hasResultJson ? (raw.resultJson || {}) : null,
        createdAt: raw.createdAt,
        hasFullPayload: hasInputJson && hasResultJson
    };
}

function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
}

export function migrateVariableScenarioFormat(inputJson) {
    if (!inputJson || typeof inputJson !== 'object') return inputJson;

    const isLegacyVariable = hasOwn(inputJson, 'annualRate') && !hasOwn(inputJson, 'euriborRate');
    if (!isLegacyVariable) return inputJson;

    const { annualRate, ...rest } = inputJson;
    return {
        ...rest,
        introPeriodYears: 5,
        introRate: annualRate,
        euriborRate: null,
        bankMargin: null,
        _legacyFormat: true
    };
}

function showMigrationMessage(calcType) {
    const normalizedType = normalizeCalcType(calcType);
    const panel = document.getElementById(`panel-${normalizedType}`);
    if (!panel) return;

    const messageEl = panel.querySelector('.calc-inline-message');
    if (!messageEl) return;

    messageEl.hidden = false;
    messageEl.dataset.tone = 'warn';
    messageEl.textContent = 'Ovaj scenario je korišćen stari format. Molimo popunite EURIBOR i marža banke pre nego što sačuvate.';
}

function formatDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('sr-RS');
}

function renderList() {
    if (!scenariosCtx.container) return;

    if (!scenariosCtx.list.length) {
        scenariosCtx.container.innerHTML = '';
        if (scenariosCtx.emptyEl) scenariosCtx.emptyEl.hidden = false;
        scenariosCtx.container.setAttribute('aria-busy', 'false');
        return;
    }

    if (scenariosCtx.emptyEl) scenariosCtx.emptyEl.hidden = true;

    const html = scenariosCtx.list.map((scenario) => `
        <article class="calc-scenario-card" data-id="${scenario.id}">
            <header class="calc-scenario-head">
                <h3>${scenario.scenarioName}</h3>
                <span class="calc-scenario-type">${scenario.calcType}</span>
            </header>
            <p class="calc-scenario-time">Sačuvano: ${formatDate(scenario.createdAt)}</p>
            <div class="calc-scenario-actions">
                <button type="button" class="calc-btn" data-scenario-action="load" data-id="${scenario.id}">Učitaj</button>
                <button type="button" class="calc-btn calc-btn-danger" data-scenario-action="delete" data-id="${scenario.id}">Obriši</button>
            </div>
        </article>
    `).join('');

    scenariosCtx.container.innerHTML = html;
    scenariosCtx.container.setAttribute('aria-busy', 'false');
}

async function fetchScenarios() {
    const scenarios = await requestScenarios({ view: SCENARIO_VIEW_META, allowFallbackWithoutView: true });
    scenariosCtx.list = scenarios.map(normalizeScenario);
    scenariosCtx.isMetaListMode = scenariosCtx.list.some((scenario) => !scenario.hasFullPayload);
    renderList();
}

function buildScenariosUrl(view) {
    if (!view) {
        return '/calculators/scenarios';
    }

    return `/calculators/scenarios?view=${encodeURIComponent(view)}`;
}

async function requestScenarios({ view = null, allowFallbackWithoutView = false } = {}) {
    const token = getAuthToken();
    const headers = {};
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const primaryUrl = buildScenariosUrl(view);

    const requestOnce = async (url) => {
        const response = await fetch(url, { headers });
        if (!response.ok) {
            throw new Error('Neuspešno učitavanje scenarija');
        }

        const payload = await response.json();
        return Array.isArray(payload.scenarios) ? payload.scenarios : [];
    };

    try {
        return await requestOnce(primaryUrl);
    } catch (error) {
        if (!allowFallbackWithoutView || view === null) {
            throw error;
        }

        return requestOnce(buildScenariosUrl(null));
    }
}

async function hydrateScenarioPayload(scenarioId) {
    const fullScenarios = await requestScenarios({ view: SCENARIO_VIEW_FULL, allowFallbackWithoutView: true });
    const normalizedFullScenarios = fullScenarios.map(normalizeScenario);

    if (normalizedFullScenarios.length) {
        const fullById = new Map(normalizedFullScenarios.map((item) => [String(item.id), item]));
        scenariosCtx.list = scenariosCtx.list.map((item) => fullById.get(String(item.id)) || item);
        scenariosCtx.isMetaListMode = scenariosCtx.list.some((item) => !item.hasFullPayload);
    }

    return scenariosCtx.list.find((item) => String(item.id) === String(scenarioId)) || null;
}

function buildScenarioName(snapshot) {
    const typeLabelMap = {
        variable: 'Varijabilna kamata',
        fixed: 'Fiksna kamata',
        [CANONICAL_YOUNG_TYPE]: 'Kredit za mlade',
        sposobnost: 'Kreditna sposobnost'
    };
    const normalizedType = normalizeCalcType(snapshot.calcType);
    const typeLabel = typeLabelMap[normalizedType] || normalizedType;
    return `${typeLabel} - ${new Date().toLocaleString('sr-RS')}`;
}

async function saveScenario(snapshot) {
    const token = getAuthToken();
    const headers = {
        'Content-Type': 'application/json'
    };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const payload = {
        calcType: normalizeCalcType(snapshot.calcType),
        scenarioName: buildScenarioName(snapshot),
        inputJson: snapshot.inputJson,
        resultJson: snapshot.resultJson
    };

    const response = await fetch('/calculators/scenario', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
    });

    if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        const message = data?.message || '';
        if (message.includes('SCENARIO_LIMIT_REACHED')) {
            setMessage('Dostigli ste maksimalan broj sačuvanih scenarija za trenutni paket.', 'warn');
            return;
        }
    }

    if (!response.ok) {
        throw new Error('Čuvanje scenarija nije uspelo');
    }

    const data = await response.json();
    scenariosCtx.list.unshift(normalizeScenario({
        ...data,
        inputJson: payload.inputJson,
        resultJson: payload.resultJson
    }));

    renderList();
    setMessage('Scenario je uspešno sačuvan.', 'success');
}

async function deleteScenario(id) {
    const token = getAuthToken();
    const headers = {};
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`/calculators/scenarios/${id}`, {
        method: 'DELETE',
        headers
    });

    if (!response.ok) {
        throw new Error('Brisanje scenarija nije uspelo');
    }

    scenariosCtx.list = scenariosCtx.list.filter((scenario) => String(scenario.id) !== String(id));
    renderList();
    setMessage('Scenario je obrisan.', 'info');
}

function bindContainerActions() {
    if (!scenariosCtx.container) return;

    scenariosCtx.container.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-scenario-action]');
        if (!button) return;

        const action = button.dataset.scenarioAction;
        const id = button.dataset.id;
        const scenario = scenariosCtx.list.find((item) => String(item.id) === String(id));
        if (!scenario) return;

        if (action === 'load') {
            try {
                let scenarioWithPayload = scenario;
                if (!scenarioWithPayload.hasFullPayload) {
                    scenarioWithPayload = await hydrateScenarioPayload(id);
                }

                if (!scenarioWithPayload || !scenarioWithPayload.hasFullPayload || !scenarioWithPayload.inputJson) {
                    throw new Error('Scenario payload missing');
                }

                let payload = scenarioWithPayload.inputJson;
                let shouldShowLegacyMigration = false;
                if (scenarioWithPayload.calcType === 'variable') {
                    const migrated = migrateVariableScenarioFormat(scenarioWithPayload.inputJson);
                    if (migrated && migrated._legacyFormat) {
                        payload = migrated;
                        shouldShowLegacyMigration = true;
                    }
                }

                loadScenarioIntoForm(scenarioWithPayload.calcType, payload);
                if (shouldShowLegacyMigration) {
                    showMigrationMessage(scenarioWithPayload.calcType);
                }
                setMessage('Scenario je učitan.', 'info');
            } catch (error) {
                setMessage('Scenario nije moguće učitati u ovom trenutku.', 'error');
            }
            return;
        }

        if (action === 'delete') {
            const confirmed = window.confirm('Da li ste sigurni da želite da obrišete scenario?');
            if (!confirmed) return;

            try {
                await deleteScenario(id);
            } catch (error) {
                setMessage('Scenario nije moguće obrisati u ovom trenutku.', 'error');
            }
        }
    });
}

function bindSaveEvents() {
    document.addEventListener('loanCalc:save-request', async (event) => {
        try {
            const detail = event.detail || getCurrentCalcSnapshot();
            await saveScenario(detail);
        } catch (error) {
            setMessage('Scenario nije sačuvan. Pokušajte ponovo.', 'error');
        }
    });
}

export async function initScenarios(state = {}) {
    scenariosCtx.state = state;
    scenariosCtx.container = document.getElementById('scenarios-list');
    scenariosCtx.emptyEl = document.querySelector('.scenarios-empty');

    if (!scenariosCtx.container) return;

    bindContainerActions();
    bindSaveEvents();

    try {
        await fetchScenarios();
    } catch (error) {
        scenariosCtx.container.setAttribute('aria-busy', 'false');
        setMessage('Scenariji trenutno nisu dostupni.', 'error');
    }
}
